<script setup>
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import EditIcons from "@/Components/Icons/EditIcons.vue";
import DeleteIcons from "@/Components/Icons/DeleteIcons.vue";
import InputField from "@/Components/InputField.vue";
import InputLabel from "@/Components/InputLabel.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import InputError from "@/Components/InputError.vue";
import Pagination from "@/Components/Pagination.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Header from "@/Components/Header.vue";
import Container from "@/Layouts/Container.vue";
import Swal from "sweetalert2";
import { useForm, usePage, router } from "@inertiajs/vue3";
import { ref, computed, watch } from "vue";
import ModalRoleUser from "@/Components/ModalRoleUser.vue";
import ModalUserPermissions from "@/Components/ModalUserPermissions.vue";
const props = defineProps({
  users: {
    type: Object,
    required: true,
  },
  roles: {
    type: Object,
    required: true,
  },
  prodis: {
    type: Object,
    required: true,
  },
  permissions: {
    type: Object,
    required: true,
  },
});

// Computed property for row number calculation with fallbacks
const getRowNumber = computed(() => {
  return (index) => {
    const currentPage = props.users?.current_page || 1;
    const perPage = props.users?.per_page || 10;
    return (currentPage - 1) * perPage + index + 1;
  };
});

const isModalAdd = ref(false);
const isModalPermissions = ref(false);
const selectedUserForPermissions = ref(null);

/**
 * Edit user function
 * @param {Object} user
 */
const editUser = (user) => {
  if (!user) {
    return;
  }

  form.id = user.id;
  form.name = user.name;
  form.email = user.email;
  form.username = user.username;
  form.role = user.role;
  form.kodeprodi = user.kodeprodi;
};
const form = useForm({
  id: null,
  name: "",
  email: "",
  username: "",
  password: "",
  role: "",
  kodeprodi: "",
});

const deleteForm = useForm({});

const submit = () => {
  const routeName = form.id ? "users.update" : "users.store";
  form[form.id ? "put" : "post"](route(routeName, form.id || {}), {
    data: {
      id: form.id,
      name: form.name,
      email: form.email,
      username: form.username,
      password: form.password,
      role: form.role,
      kodeprodi: form.kodeprodi,
    },
    onSuccess: () => {
      form.reset();
      Swal.fire({
        title: "Berhasil!",
        text: `Data berhasil ${form.id ? "diperbarui" : "disimpan"}.`,
        icon: "success",
        confirmButtonText: "OK",
      });
    },
    onError: (errors) => {
      Swal.fire({
        title: "Gagal!",
        text: "Terjadi kesalahan saat mengirim data.",
        icon: "error",
        confirmButtonText: "Coba Lagi",
      });
    },
  });
};
/**
 * Delete user function
 * @param {number} userId
 */
const deleteUsers = (userId) => {
  Swal.fire({
    title: "Apakah Anda yakin?",
    text: "User ini akan dihapus secara permanen!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  }).then((result) => {
    if (result.isConfirmed) {
      deleteForm.delete(route("users.destroy", userId), {
        onSuccess: () => {
          Swal.fire({
            title: "Berhasil!",
            text: "User berhasil dihapus.",
            icon: "success",
            confirmButtonText: "OK",
          });
        },
        onError: (errors) => {
          Swal.fire({
            title: "Gagal!",
            text: "Terjadi kesalahan saat menghapus user.",
            icon: "error",
            confirmButtonText: "OK",
          });
        },
      });
    }
  });
};
const showuser = ref(null);
const showrole = ref(null);

const roleUser = (user) => {
  showuser.value = user;
  showrole.value = props.roles;
  isModalAdd.value = true;
};

const managePermissions = (user) => {
  selectedUserForPermissions.value = user;
  isModalPermissions.value = true;
};

/**
 * Pagination and search functionality
 */
let search = ref(usePage().props.search || ""),
  pageNumber = ref(1);

let studentUrl = computed(() => {
  let url = new URL(route("users.index"));
  url.searchParams.append("page", pageNumber.value);
  if (search.value) {
    url.searchParams.append("search", search.value);
  }

  return url;
});

const updatedPageNumber = (link) => {
  if (link && link.url) {
    try {
      const urlParams = new URLSearchParams(link.url.split("?")[1] || "");
      const page = urlParams.get("page");
      if (page) {
        pageNumber.value = parseInt(page);
      }
    } catch (error) {
      console.error("Error parsing pagination URL:", error);
    }
  }
};

watch(
  () => studentUrl.value,
  (updatedStudentUrl) => {
    router.visit(updatedStudentUrl.toString(), {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
  }
);

watch(
  () => search.value,
  (value) => {
    if (value) {
      pageNumber.value = 1; // Reset to page 1 when search is updated
    }
  }
);
</script>

<template>
  <Head title="Pengguna" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Pengguna', href: route('users.index') },
          ]"
        />

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div class="order-2 col-span-1 space-y-6 lg:order-1">
            <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-users sm:mr-3"></i>
                  {{ form.id ? "Edit User" : "Tambah User" }}
                </h2>
              </div>
              <div class="p-4 sm:p-6">
                <form class="space-y-4" @submit.prevent="submit">
                  <!-- Input Fields -->
                  <InputField label="Nama" icon="pen" v-model="form.name" />
                  <InputError :message="form.errors.name" class="mt-2" />

                  <InputField
                    label="Email"
                    type="email"
                    icon="at"
                    v-model="form.email"
                  />
                  <InputError :message="form.errors.email" class="mt-2" />

                  <InputField
                    label="Username"
                    icon="user"
                    v-model="form.username"
                  />
                  <InputError :message="form.errors.username" class="mt-2" />

                  <InputField
                    label="Password"
                    type="password"
                    icon="key"
                    v-model="form.password"
                  />
                  <InputError :message="form.errors.password" class="mt-2" />

                  <!-- Roles Dropdown -->
                  <InputLabel for="Roles" value="Roles" class="mb-2" />
                  <select
                    v-model="form.role"
                    id="role"
                    class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                  >
                    <option value="">Pilih Roles</option>
                    <option
                      v-for="role in roles.data"
                      :key="role.id"
                      :value="role.id"
                    >
                      {{ role.title }}
                    </option>
                  </select>
                  <InputError :message="form.errors.role" class="mt-2" />

                  <!-- Program Studi Dropdown -->
                  <InputLabel
                    for="Program Studi"
                    value="Program Studi"
                    class="mb-2"
                  />
                  <select
                    v-model="form.kodeprodi"
                    class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                  >
                    <option value="">Pilih Program Studi</option>
                    <option
                      v-for="prodi in prodis.data"
                      :key="prodi.id"
                      :value="prodi.kodeprodi"
                    >
                      {{ prodi.jenjang }} - {{ prodi.name }}
                    </option>
                  </select>
                  <InputError :message="form.errors.kodeprodi" class="mt-2" />

                  <!-- Submit Button -->
                  <div class="pt-4">
                    <PrimaryBtn
                      :disabled="form.processing"
                      class="w-full px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-floppy-disk"></i>
                      {{ form.processing ? "Menyimpan..." : "Simpan" }}
                    </PrimaryBtn>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div class="order-1 col-span-1 lg:col-span-2 lg:order-2">
            <!-- Header Section -->
            <div class="mb-4 sm:mb-6">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    Daftar Pengguna
                  </h3>
                  <p class="mt-1 text-sm text-gray-600">
                    Kelola pengguna sistem dan akses kontrol
                  </p>
                </div>
                <div class="mt-2 text-sm text-gray-500 sm:mt-0">
                  Total: {{ users.total }} pengguna
                </div>
              </div>
            </div>

            <!-- Search Input -->
            <div class="relative mb-4">
              <div
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <MagnifyingGlassVue class="w-4 h-4 text-gray-400" />
              </div>
              <input
                v-model="search"
                type="text"
                autocomplete="off"
                placeholder="Search data..."
                id="search"
                class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
              />
            </div>

            <!-- Table -->
            <div
              class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
            >
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 min-w-[150px]"
                      >
                        Nama
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell min-w-[200px]"
                      >
                        Email
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden sm:table-cell min-w-[120px]"
                      >
                        Username
                      </th>
                      <th
                        scope="col"
                        class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[160px]"
                      >
                        <span class="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <!-- Table Rows -->
                    <tr
                      v-for="(user, index) in users.data"
                      :key="user.id"
                      class="transition-colors duration-150 hover:bg-gray-50"
                    >
                      <td
                        class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                      >
                        {{ getRowNumber(index) }}
                      </td>
                      <td class="px-3 py-4 text-sm">
                        <div class="flex items-center">
                          <span class="mr-2 text-indigo-600">👤</span>
                          <span class="font-medium text-gray-900">
                            {{ user.name }}
                          </span>
                        </div>
                        <!-- Mobile view: Show email under name on small screens -->
                        <div class="mt-1 text-xs text-gray-500 md:hidden">
                          {{ user.email }}
                        </div>
                        <!-- Mobile view: Show username under email on small screens -->
                        <div class="mt-1 text-xs text-gray-500 sm:hidden">
                          @{{ user.username }}
                        </div>
                      </td>
                      <td
                        class="hidden px-3 py-4 text-sm text-gray-500 whitespace-nowrap md:table-cell"
                      >
                        {{ user.email }}
                      </td>
                      <td
                        class="hidden px-3 py-4 text-sm text-gray-500 whitespace-nowrap sm:table-cell"
                      >
                        @{{ user.username }}
                      </td>
                      <td
                        class="relative py-4 pl-3 pr-4 text-sm font-medium text-right sm:pr-6"
                      >
                        <div
                          class="flex items-center justify-end space-x-1 sm:space-x-2"
                        >
                          <!-- Action Buttons -->
                          <button
                            @click="managePermissions(user)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-100 rounded-md hover:bg-green-200 hover:text-green-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Manage Permissions"
                          >
                            <i class="w-3 h-3 fa-solid fa-key"></i>
                            <span class="hidden ml-1 sm:inline"
                              >Permissions</span
                            >
                          </button>
                          <Link
                            :href="route('users.show', user.iduser)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200 hover:text-indigo-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Edit"
                          >
                            <EditIcons class="w-3 h-3" />
                            <span class="hidden ml-1 sm:inline">Edit</span>
                          </Link>
                          <button
                            @click="deleteUsers(user.id)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 hover:text-red-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Delete"
                          >
                            <DeleteIcons class="w-3 h-3" />
                            <span class="hidden ml-1 sm:inline">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <ModalRoleUser
              v-model:isOpen="isModalAdd"
              :title="form.id ? 'Edit Role' : 'Tambah Role'"
              :user="showuser"
              :roles="showrole"
            />

            <ModalUserPermissions
              v-model:isOpen="isModalPermissions"
              title="Manage User Permissions"
              :user="selectedUserForPermissions"
            />

            <!-- Pagination -->
            <Pagination :data="users" :updatedPageNumber="updatedPageNumber" />
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>
